import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Forside", href: "/" },
    { label: "Rottespærre", href: "/rottespaerre" },
    { label: "Højvandslukke", href: "/hoejvandslukke" },
    { label: "Kloak TV", href: "/kloak-tv" },
    { label: "Punktreparation", href: "/punktreparation" },
    { label: "Spuling/slamsugning", href: "/spuling-slamsugning" },
    { label: "Råd Mod Rotter", href: "/raad-mod-rotter" },
    { label: "Om Fyns Afløbsteknik", href: "/om-fyns-afloebsteknik" },
    { label: "Kontakt", href: "/kontakt" }
  ];

  const logoUrl = "https://assets.auxodev.dk/assets/3ee58f8c-8fdb-408d-9efa-3b231a487177/cropped-fyat-logo-1-180x180.png";

  return (
    <>
    <header className="w-full sticky top-0 z-50 bg-neutral-950 text-white shadow-xl border-b border-neutral-800 transition-all duration-200">
      {/* Top bar med kontaktinfo og autorisationsstatus */}
      <div className="hidden sm:block border-b border-neutral-800/80 bg-[#0a0d12] text-xs text-neutral-400">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 hidden sm:flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-4 sm:gap-6 flex-wrap">
            <span className="flex items-center gap-1.5 text-neutral-300">
              <i className="fa-solid fa-certificate text-neutral-400" aria-hidden="true" />
              <span className="font-semibold text-neutral-200">Aut. Kloakmester</span>
              <span className="hidden sm:inline text-neutral-500">• Brolandvej 78, 5320 Agedrup</span>
            </span>
            <span className="hidden md:inline text-neutral-700">|</span>
            <span className="hidden md:flex items-center gap-1.5 text-neutral-400">
              <i className="fa-solid fa-clock text-neutral-500" aria-hidden="true" />
              <span>Døgnvagt & hurtig udrykning på Fyn</span>
            </span>
          </div>

          <div className="flex items-center gap-4 sm:gap-6">
            <a
              href="mailto:mc@fyat.dk"
              className="hover:text-white transition-colors flex items-center gap-1.5 no-break text-neutral-400"
            >
              <i className="fa-solid fa-envelope text-neutral-500" aria-hidden="true" />
              <span className="no-break">mc@fyat.dk</span>
            </a>
            <a
              href="tel:50493950"
              className="font-bold text-neutral-200 hover:text-white transition-colors flex items-center gap-1.5 no-break"
            >
              <i className="fa-solid fa-phone text-neutral-400" aria-hidden="true" />
              <span className="no-break">50 49 39 50</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation */}
      <div className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-200 ${isScrolled ? "py-2.5" : "py-3.5"}`}>
        <div className="flex items-center justify-between gap-4">
          {/* Logo - grå/sølv logo */}
          <Link to="/" className="flex items-center gap-2.5 sm:gap-3 min-w-0 shrink focus:outline-none group">
            <img
              src={logoUrl}
              alt="Fyns Afløbsteknik ApS Logo"
              className="h-14 w-14 sm:h-16 sm:w-16 md:h-20 md:w-20 object-contain shrink-0 brightness-0 invert opacity-75 contrast-125 group-hover:opacity-95 transition-all duration-200"
              style={{ filter: "brightness(0) invert(0.8) contrast(1.1)" }}
            />
          </Link>

          {/* Desktop Navigation - synlig ved viewport bredde 1200px og opefter */}
          <nav className="hidden min-[1200px]:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive =
                link.href === "/"
                  ? location.pathname === "/"
                  : location.pathname === link.href || location.pathname.startsWith(link.href + "/");

              return (
                <NavLink
                  key={link.href}
                  to={link.href}
                  className={`px-2 xl:px-3 py-2 text-xs xl:text-sm font-medium rounded transition-all duration-150 relative whitespace-nowrap ${
                    isActive
                      ? "text-white bg-neutral-800/80 font-bold shadow-xs border border-neutral-700/60"
                      : "text-neutral-300 hover:text-white hover:bg-neutral-800/50"
                  }`}
                >
                  {link.label}
                  {isActive && (
                    <span className="absolute bottom-0 left-3 right-3 h-0.5 bg-neutral-300 rounded-full" />
                  )}
                </NavLink>
              );
            })}

            {/* Kontakt CTA Button removed as requested */}
          </nav>

          {/* Mobil & Tablet Header CTA + Burger (synlig under 1200px) */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0 min-[1200px]:hidden">
            <a
              href="tel:50493950"
              className="bg-neutral-100 text-neutral-900 hover:bg-white font-bold text-xs sm:text-sm px-2.5 sm:px-3 py-2 rounded shadow transition-colors flex items-center gap-1.5 no-break"
              title="Ring 50 49 39 50"
            >
              <i className="fa-solid fa-phone text-neutral-800 text-xs sm:text-sm" aria-hidden="true" />
              <span className="no-break hidden sm:inline">50 49 39 50</span>
              <span className="no-break sm:hidden">Ring op</span>
            </a>

            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 sm:p-2.5 rounded-lg text-neutral-300 hover:text-white bg-neutral-900 hover:bg-neutral-800 focus:outline-none focus:ring-2 focus:ring-neutral-400 shrink-0 cursor-pointer border border-neutral-700"
              aria-label={mobileMenuOpen ? "Luk menu" : "Åbn menu"}
              aria-expanded={mobileMenuOpen}
            >
              <i className={`fa-solid ${mobileMenuOpen ? "fa-xmark" : "fa-bars"} text-lg sm:text-xl`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobil Menu Overlay (kun under 1200px) */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden border-t border-neutral-800 bg-neutral-950 px-4 pt-3 pb-6 space-y-2 shadow-2xl animate-in slide-in-from-top-2 duration-200">
          <nav className="flex flex-col space-y-1">
            {navLinks.map((link) => {
              const isActive =
                link.href === "/"
                  ? location.pathname === "/"
                  : location.pathname === link.href || location.pathname.startsWith(link.href + "/");

              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded text-base font-medium flex items-center justify-between transition-colors ${
                    isActive
                      ? "bg-neutral-900 text-white font-bold border-l-4 border-neutral-300"
                      : "text-neutral-300 hover:text-white hover:bg-neutral-900/60"
                  }`}
                >
                  <span>{link.label}</span>
                  <i className="fa-solid fa-chevron-right text-xs text-neutral-500" aria-hidden="true" />
                </Link>
              );
            })}
          </nav>

          <div className="pt-4 mt-2 border-t border-neutral-800 grid grid-cols-1 sm:grid-cols-2 gap-2">
            <a
              href="tel:50493950"
              className="w-full bg-neutral-100 hover:bg-white text-neutral-900 text-center font-bold py-3 px-4 rounded transition-colors flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span className="no-break">Ring 50 49 39 50</span>
            </a>
            <Link
              to="/kontakt"
              onClick={() => setMobileMenuOpen(false)}
              className="w-full bg-neutral-900 hover:bg-neutral-800 text-white border border-neutral-700 text-center font-medium py-3 px-4 rounded transition-colors flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-envelope" aria-hidden="true" />
              <span>Skriv til os</span>
            </Link>
          </div>
        </div>
      )}
    </header>

    {/* Fixed floating CTA buttons: phone, email with smooth transition & centered icons */}
    <div
      aria-label="Hurtig kontakt"
      className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 pointer-events-auto"
    >
      {/* Telefon CTA knap */}
      <a
        href="tel:50493950"
        aria-label="Ring 50 49 39 50"
        title="Ring til 50 49 39 50"
        className="w-13 h-13 sm:w-14 sm:h-14 rounded-full bg-white text-neutral-950 border border-neutral-300 shadow-xl flex items-center justify-center text-lg sm:text-xl transition-all duration-300 ease-in-out hover:scale-110 hover:shadow-2xl hover:bg-neutral-100 hover:border-neutral-400 group cursor-pointer focus:outline-none focus:ring-4 focus:ring-neutral-400/40"
      >
        <i
          className="fa-solid fa-phone text-neutral-950 group-hover:scale-110 transition-transform duration-300"
          aria-hidden="true"
        />
      </a>

      {/* E-mail CTA knap */}
      <a
        href="mailto:mc@fyat.dk"
        aria-label="Send e-mail til mc@fyat.dk"
        title="Send e-mail til mc@fyat.dk"
        className="w-13 h-13 sm:w-14 sm:h-14 rounded-full bg-neutral-900 text-neutral-100 border border-neutral-700 shadow-xl flex items-center justify-center text-lg sm:text-xl transition-all duration-300 ease-in-out hover:scale-110 hover:shadow-2xl hover:bg-neutral-800 hover:border-neutral-500 hover:text-white group cursor-pointer focus:outline-none focus:ring-4 focus:ring-neutral-600/40"
      >
        <i
          className="fa-solid fa-envelope text-neutral-200 group-hover:scale-110 transition-transform duration-300"
          aria-hidden="true"
        />
      </a>
    </div>
    </>
  );
}
